import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  HostListener
} from '@angular/core';
import { IVideo } from '../../interfaces/videosByCategory';
import {
  IDateLabel,
  VideoDateLabelFormatterService
} from '../../services/video-date-label-formatter/video-date-label-formatter.service';
import { VideoModalHandlerService } from '../../services/video-modal-handler/video-modal-handler.service';
import { YoutubeDataExtractorService } from '../../services/youtube-data-extractor/youtube-data-extractor.service';
import { LiveVideoSubscriptionService } from 'src/app/services/videos-and-events/live-video-subscription.service';
import { ToastrService } from 'ngx-toastr';
import { SafeResourceUrl } from '@angular/platform-browser';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-live-banner',
  templateUrl: './live-banner.component.html',
  styleUrl: './live-banner.component.scss'
})
export class LiveBannerComponent implements OnChanges, OnDestroy {
  @Input() banner: IVideo;
  @Input() isLive: boolean;
  dateLabel: IDateLabel;
  embedUrl: SafeResourceUrl | null;
  isLiveModalOpen: boolean = false;
  isMobile: boolean = false;
  isNotified: boolean = false;
  isSubscribing: boolean = false;
  showCountdown: boolean = false;
  countdownExpired: boolean = false;
  countdown: string = '';
  private modalSubscription: Subscription;
  private countdownInterval: any;

  constructor(
    private videoDateLabelFormatterService: VideoDateLabelFormatterService,
    private videoModalHandlerService: VideoModalHandlerService,
    private youtubeExtractor: YoutubeDataExtractorService,
    private liveVideoSubscriptionService: LiveVideoSubscriptionService,
    private toastr: ToastrService
  ) {
    this.modalSubscription =
      this.videoModalHandlerService.modalState$.subscribe((isOpen) => {
        this.isLiveModalOpen = isOpen;
      });
    this.checkScreenSize();
  }

  @HostListener('window:resize')
  onResize() {
    this.checkScreenSize();
  }

  private checkScreenSize() {
    this.isMobile = window.innerWidth < 900;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['banner'] && this.banner) {
      this.dateLabel = this.videoDateLabelFormatterService.formatDateLabel(
        this.banner
      );
      this.handleLive();
      if (this.dateLabel.type === 'soon') {
        this.checkSubscription();
        this.initCountdown();
      }
    }
  }

  private initCountdown(): void {
    if (!this.banner.dateTimeTransmissao) return;

    const transmission = new Date(this.banner.dateTimeTransmissao);
    const now = new Date();

    const isToday =
      transmission.getFullYear() === now.getFullYear() &&
      transmission.getMonth() === now.getMonth() &&
      transmission.getDate() === now.getDate();

    const afterSixAM = now.getHours() >= 6;

    if (!isToday || !afterSixAM) return;

    this.showCountdown = true;
    this.tick(transmission);
    this.countdownInterval = setInterval(() => this.tick(transmission), 1000);
  }

  private tick(transmission: Date): void {
    const diff = transmission.getTime() - Date.now();

    if (diff <= 0) {
      this.showCountdown = false;
      this.countdownExpired = true;
      clearInterval(this.countdownInterval);
      return;
    }

    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);

    this.countdown = [hours, minutes, seconds]
      .map((v) => String(v).padStart(2, '0'))
      .join(':');
  }

  private checkSubscription(): void {
    this.liveVideoSubscriptionService
      .checkSubscription([this.banner.id])
      .subscribe({
        next: (statuses) => {
          const status = statuses.find((s) => s.eventId === this.banner.id);
          this.isNotified = status?.isSubscribed ?? false;
        }
      });
  }

  handleLive() {
    if (this.dateLabel.type !== 'live') return;
    const videoId = this.youtubeExtractor.extractYoutubeId(
      this.banner.videoURL
    );
    const embedUrl = this.youtubeExtractor.prepareEmbededUrl(videoId, {
      autoplay: 1,
      mute: 1,
      controls: 0
    });
    this.embedUrl = embedUrl;
  }

  onButtonClick(video: IVideo) {
    if (this.dateLabel.type === 'soon') {
      this.isSubscribing = true;
      this.liveVideoSubscriptionService.subscribe([video.id]).subscribe({
        next: () => {
          this.isNotified = true;
          this.isSubscribing = false;
          this.toastr.success('Lembrete registrado com sucesso!');
        },
        error: () => {
          this.isSubscribing = false;
        }
      });
      return;
    }
    this.videoModalHandlerService.openVideoModal(video, this.isLive);
  }

  ngOnDestroy(): void {
    if (this.modalSubscription) {
      this.modalSubscription.unsubscribe();
    }
    clearInterval(this.countdownInterval);
  }
}
